import { TabelaDetalhes } from './components/TabelaDetalhes';
import { TabelaPorFuncao } from './components/TabelaPorFuncao';
import { TabelaPorLocal } from './components/TabelaPorLocal';
import { TabelaPorMes } from './components/TabelaPorMes';
import { GraficoPorMes } from './components/GraficoPorMes';
import { formatarReajusteLinha, TabelaReajustesFuncao } from './components/TabelaReajustesFuncao';
import { ReajusteEmLote } from './components/ReajusteEmLote';
import { SimuladorFolhaPDF } from './reports/SimuladorFolhaPDF';
import { readFuncao } from '@/modules/structure/funcao/funcao.service';
import { simularFolha } from './simulador-folha.service';
import { podeAcessarSimuladorFolha } from './simulador-folha.access';
import { ReajusteFuncaoLinha, SimuladorFolhaResultado, TipoReajuste } from './simulador-folha.types';
import Loading from '@/shared/components/Carregando';
import Links from '@/shared/constants/navegacao';
import { useNavigationView } from '@/shared/hooks/useNavigationView';
import { exportFromArrayToExcel } from '@/shared/utils/xlsx';
import { exportFromJsxToPdf } from '@/shared/utils/pdf';
import { formatarMoeda } from '@/shared/utils/numeros';
import { mesAnoParaPrimeiroDia, extrairYyyyMm, formatarYyyyMmMesExtensoOuIndefinido } from '@/shared/utils/datas';
import { toast } from 'react-toastify';
import { Collapse } from 'react-bootstrap';
import { Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';

type AbaAtiva = 'funcao' | 'local' | 'detalhes' | 'mes';

function labelCompetencia(mesAno: string): string {
  const [ano, mes] = mesAno.split('-');
  return `${mes}/${ano}`;
}

export default function SimuladorFolhaPage() {
  const { effectiveView, viewReady } = useNavigationView();
  const hoje = new Date();
  const mesPadrao = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;

  const [competencia, setCompetencia] = useState(mesPadrao);
  const [linhasReajuste, setLinhasReajuste] = useState<ReajusteFuncaoLinha[]>([]);
  const [carregandoFuncoes, setCarregandoFuncoes] = useState(true);
  const [resultado, setResultado] = useState<SimuladorFolhaResultado | null>(null);
  const [abaAtiva, setAbaAtiva] = useState<AbaAtiva>('funcao');
  const [carregando, setCarregando] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [parametrosAbertos, setParametrosAbertos] = useState(true);

  const carregarFuncoes = () => {
    setCarregandoFuncoes(true);
    readFuncao({
      take: 1000,
      orderBy: 'ASC',
      orderField: 'nome',
      competencia: mesAnoParaPrimeiroDia(competencia),
    })
      .then(([data]) => {
        setLinhasReajuste((prev) => {
          const reajustesAnteriores = new Map(
            prev.map((l) => [l.funcaoId, { tipoReajuste: l.tipoReajuste, valorReajuste: l.valorReajuste }]),
          );
          return data.map((f) => {
            const anterior = reajustesAnteriores.get(f.id);
            return {
              funcaoId: f.id,
              funcaoNome: f.nome,
              ultimoSalarioVigente: f.ultimoSalarioVigente,
              tipoReajuste: anterior?.tipoReajuste ?? 'PERCENTUAL',
              valorReajuste: anterior?.valorReajuste ?? 0,
            };
          });
        });
      })
      .catch(() => {
        setLinhasReajuste([]);
        toast.error('Falha ao carregar funções.');
      })
      .finally(() => setCarregandoFuncoes(false));
  };

  useEffect(() => {
    carregarFuncoes();
  }, [competencia]);

  const handleReajusteChange = (
    funcaoId: number,
    campo: 'tipoReajuste' | 'valorReajuste',
    valor: string,
  ) => {
    setLinhasReajuste((prev) =>
      prev.map((linha) => {
        if (linha.funcaoId !== funcaoId) return linha;
        if (campo === 'tipoReajuste') {
          return { ...linha, tipoReajuste: valor as ReajusteFuncaoLinha['tipoReajuste'] };
        }
        const numero = valor === '' ? 0 : Number(valor);
        return { ...linha, valorReajuste: Number.isNaN(numero) ? 0 : numero };
      }),
    );
  };

  const handleAplicarReajusteATodos = (tipoReajuste: TipoReajuste, valorReajuste: number) => {
    if (valorReajuste < 0 || Number.isNaN(valorReajuste)) {
      toast.error('Informe um valor de reajuste válido.');
      return;
    }
    setLinhasReajuste((prev) =>
      prev.map((linha) => ({ ...linha, tipoReajuste, valorReajuste })),
    );
  };

  const handleSimular = async () => {
    if (linhasReajuste.length === 0) {
      toast.error('Nenhuma função cadastrada para simular.');
      return;
    }

    const invalida = linhasReajuste.find((l) => l.valorReajuste < 0 || Number.isNaN(l.valorReajuste));
    if (invalida) {
      toast.error(`Informe um valor de reajuste válido para "${invalida.funcaoNome}".`);
      return;
    }

    setCarregando(true);
    try {
      const data = await simularFolha({
        competencia: mesAnoParaPrimeiroDia(competencia),
        reajustes: linhasReajuste.map((l) => ({
          funcaoId: l.funcaoId,
          tipoReajuste: l.tipoReajuste,
          valorReajuste: l.valorReajuste,
        })),
      });
      setResultado(data);
      if (data.resumo.quantidadeExcluidos > 0) {
        toast.warning(
          `${data.resumo.quantidadeExcluidos} servidor(es) excluído(s) por falta de salário vigente.`,
        );
      }
    } catch (error) {
      toast.error(String(error));
    } finally {
      setCarregando(false);
    }
  };

  const exportarExcel = () => {
    if (!resultado) return;

    const resumoRows = [
      ['Folha atual', formatarMoeda(resultado.resumo.totalAtual)],
      ['Folha projetada', formatarMoeda(resultado.resumo.totalProjetado)],
      ['Diferença', formatarMoeda(resultado.resumo.diferenca)],
      ['Servidores', String(resultado.resumo.quantidadeServidores)],
    ];

    exportFromArrayToExcel(
      ['Indicador', 'Valor'],
      [
        ...resumoRows,
        [],
        ['Reajustes por função'],
        ['Função', 'Tipo', 'Valor', 'Salário vigente'],
        ...linhasReajuste.map((l) => [
          l.funcaoNome,
          l.tipoReajuste === 'PERCENTUAL' ? 'Percentual' : 'Valor fixo',
          formatarReajusteLinha(l),
          l.ultimoSalarioVigente != null ? formatarMoeda(l.ultimoSalarioVigente) : '—',
        ]),
        [
          'Total',
          '',
          '',
          formatarMoeda(
            linhasReajuste.reduce((acc, l) => acc + (l.ultimoSalarioVigente ?? 0), 0),
          ),
        ],
        [],
        ['Por função'],
        ['Função', 'Qtd.', 'Atual', 'Projetado', 'Diferença'],
        ...resultado.porFuncao.map((i) => [
          i.funcaoNome,
          String(i.quantidade),
          formatarMoeda(i.totalAtual),
          formatarMoeda(i.totalProjetado),
          formatarMoeda(i.diferenca),
        ]),
        [
          'Total',
          String(resultado.resumo.quantidadeServidores),
          formatarMoeda(resultado.resumo.totalAtual),
          formatarMoeda(resultado.resumo.totalProjetado),
          formatarMoeda(resultado.resumo.diferenca),
        ],
        [],
        ['Por local'],
        ['Local', 'Qtd.', 'Atual', 'Projetado', 'Diferença'],
        ...resultado.porLocal.map((i) => [
          i.localNome,
          String(i.quantidade),
          formatarMoeda(i.totalAtual),
          formatarMoeda(i.totalProjetado),
          formatarMoeda(i.diferenca),
        ]),
        [
          'Total',
          String(resultado.resumo.quantidadeServidores),
          formatarMoeda(resultado.resumo.totalAtual),
          formatarMoeda(resultado.resumo.totalProjetado),
          formatarMoeda(resultado.resumo.diferenca),
        ],
        [],
        ['Detalhado'],
        ['Nome', 'Tipo', 'Função', 'Local', 'Atual', 'Projetado', 'Diferença'],
        ...resultado.detalhes.map((i) => [
          i.nome,
          i.tipo === 'defensor' ? 'Defensor' : 'Colaborador',
          i.funcaoNome,
          i.localNome,
          formatarMoeda(i.valorAtual),
          formatarMoeda(i.valorProjetado),
          formatarMoeda(i.diferenca),
        ]),
        [
          'Total',
          '',
          '',
          '',
          formatarMoeda(resultado.resumo.totalAtual),
          formatarMoeda(resultado.resumo.totalProjetado),
          formatarMoeda(resultado.resumo.diferenca),
        ],
        [],
        ['Projeção mensal (ano inteiro)'],
        ['Mês', 'Qtd.', 'Atual', 'Projetado', 'Diferença'],
        ...resultado.porMes.map((i) => [
          formatarYyyyMmMesExtensoOuIndefinido(extrairYyyyMm(i.competencia)),
          String(i.quantidadeServidores),
          formatarMoeda(i.totalAtual),
          formatarMoeda(i.totalProjetado),
          formatarMoeda(i.diferenca),
        ]),
        [
          'Total acumulado',
          '',
          formatarMoeda(resultado.porMes.reduce((acc, i) => acc + i.totalAtual, 0)),
          formatarMoeda(resultado.porMes.reduce((acc, i) => acc + i.totalProjetado, 0)),
          formatarMoeda(resultado.porMes.reduce((acc, i) => acc + i.diferenca, 0)),
        ],
      ],
    );
  };

  const exportarPdf = async () => {
    if (!resultado) return;
    setExportando(true);
    try {
      await exportFromJsxToPdf(
        <SimuladorFolhaPDF
          resultado={resultado}
          competenciaLabel={labelCompetencia(competencia)}
          reajustes={linhasReajuste}
        />,
      );
    } finally {
      setExportando(false);
    }
  };

  if (!viewReady) return <Loading />;
  if (!podeAcessarSimuladorFolha(effectiveView)) {
    return <Navigate to={Links.Nao_Autorizado} replace />;
  }

  return (
    <>
      <div className="row">
        <div className="col-12">
          <h3 className="pt-4">Simulador de Folha</h3>
          <p className="text-muted">
            Informe o reajuste por função e simule o impacto na folha de pagamento de todos os servidores.
          </p>
        </div>
      </div>
      <hr className="pb-3" />

      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center py-2">
          <span className="fw-semibold">
            Parâmetros da simulação
            {!parametrosAbertos && (
              <span className="text-muted fw-normal ms-2">
                · Competência {labelCompetencia(competencia)}
              </span>
            )}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setParametrosAbertos((aberto) => !aberto)}
            aria-expanded={parametrosAbertos}
            aria-controls="collapse-parametros-simulador"
          >
            {parametrosAbertos ? 'Recolher' : 'Expandir'}
          </button>
        </div>
        <Collapse in={parametrosAbertos}>
          <div id="collapse-parametros-simulador">
            <div className="card-body">
              <div className="row g-2 g-md-3 align-items-end mb-3">
                <div className="col-md-3 col-lg-2">
                  <label htmlFor="competencia" className="form-label">Competência</label>
                  <input
                    id="competencia"
                    type="month"
                    className="form-control form-control-sm"
                    value={competencia}
                    onChange={(e) => setCompetencia(e.target.value)}
                  />
                </div>
                {!carregandoFuncoes && (
                  <ReajusteEmLote
                    disabled={linhasReajuste.length === 0}
                    onAplicar={handleAplicarReajusteATodos}
                  />
                )}
                <div className="col-md-3 col-lg-2">
                  <label className="form-label d-none d-md-block">&nbsp;</label>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm w-100"
                    disabled={carregando || carregandoFuncoes || linhasReajuste.length === 0}
                    onClick={() => void handleSimular()}
                  >
                    {carregando ? 'Simulando...' : 'Simular'}
                  </button>
                </div>
              </div>

              {carregandoFuncoes ? (
                <p className="text-muted mb-0">Carregando funções...</p>
              ) : (
                <TabelaReajustesFuncao linhas={linhasReajuste} onChange={handleReajusteChange} />
              )}
            </div>
          </div>
        </Collapse>
      </div>

      {resultado && (
        <>
          <div className="d-flex justify-content-end gap-2 mb-3">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={exportando}
              onClick={exportarExcel}
            >
              Exportar Excel
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={exportando}
              onClick={() => void exportarPdf()}
            >
              {exportando ? 'Gerando PDF...' : 'Exportar PDF'}
            </button>
          </div>

          <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${abaAtiva === 'funcao' ? 'active' : ''}`}
                onClick={() => setAbaAtiva('funcao')}
              >
                Por função (cargo)
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${abaAtiva === 'local' ? 'active' : ''}`}
                onClick={() => setAbaAtiva('local')}
              >
                Por local de lotação
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${abaAtiva === 'detalhes' ? 'active' : ''}`}
                onClick={() => setAbaAtiva('detalhes')}
              >
                Detalhado
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${abaAtiva === 'mes' ? 'active' : ''}`}
                onClick={() => setAbaAtiva('mes')}
              >
                Por mês (ano inteiro)
              </button>
            </li>
          </ul>

          {abaAtiva === 'funcao' && (
            <TabelaPorFuncao dados={resultado.porFuncao} resumo={resultado.resumo} />
          )}
          {abaAtiva === 'local' && (
            <TabelaPorLocal dados={resultado.porLocal} resumo={resultado.resumo} />
          )}
          {abaAtiva === 'detalhes' && (
            <TabelaDetalhes dados={resultado.detalhes} resumo={resultado.resumo} />
          )}
          {abaAtiva === 'mes' && (
            <>
              <GraficoPorMes dados={resultado.porMes} />
              <TabelaPorMes dados={resultado.porMes} />
            </>
          )}
        </>
      )}
    </>
  );
}
